import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { createAdminApiClient } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from "../../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { toast } from "sonner";
import { Eye, Trash2, Mail, Phone, Building, FileText, Calendar, RefreshCw } from "lucide-react";
import { format } from "date-fns";

const AdminQuotes = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [quotes, setQuotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedQuote, setSelectedQuote] = useState(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [notes, setNotes] = useState("");
    
    const statusFilter = searchParams.get("status") || "";
    
    const getAdminApi = () => {
        const credStr = sessionStorage.getItem('adminCredentials');
        if (!credStr) return null;
        const credentials = JSON.parse(credStr);
        return createAdminApiClient(credentials.username, credentials.password);
    };
    
    const fetchQuotes = async () => {
        setLoading(true);
        try {
            const adminApi = getAdminApi();
            if (!adminApi) {
                setLoading(false);
                return;
            }
            const res = await adminApi.getQuotes(statusFilter || undefined);
            setQuotes(res.data);
        } catch (err) {
            console.error("Failed to fetch quotes:", err);
            toast.error("Failed to load quote requests");
        } finally {
            setLoading(false);
        }
    };
    
    useEffect(() => {
        fetchQuotes();
    }, [statusFilter]);
    
    const handleStatusChange = (status) => {
        if (status && status !== "all") {
            setSearchParams({ status });
        } else {
            setSearchParams({});
        }
    };
    
    const handleView = (quote) => {
        setSelectedQuote(quote);
        setNotes(quote.notes || "");
        setIsDialogOpen(true);
    };
    
    const handleUpdateStatus = async (status) => {
        try {
            const adminApi = getAdminApi();
            await adminApi.updateQuote(selectedQuote.id, status, notes);
            toast.success("Quote updated");
            setIsDialogOpen(false);
            fetchQuotes();
        } catch (err) {
            console.error("Failed to update quote:", err);
            toast.error("Failed to update quote");
        }
    };
    
    const handleDelete = async (quoteId) => {
        if (!window.confirm("Are you sure you want to delete this quote request?")) return;
        
        try {
            const adminApi = getAdminApi();
            await adminApi.deleteQuote(quoteId);
            toast.success("Quote deleted");
            fetchQuotes();
        } catch (err) {
            console.error("Failed to delete quote:", err);
            toast.error("Failed to delete quote");
        }
    };
    
    const getStatusBadge = (status) => {
        const styles = {
            new: "bg-yellow-500/10 text-yellow-500",
            contacted: "bg-blue-500/10 text-blue-500",
            quoted: "bg-purple-500/10 text-purple-500",
            closed: "bg-green-500/10 text-green-500"
        };
        return (
            <Badge className={`rounded-none ${styles[status] || 'bg-secondary text-secondary-foreground'}`}>
                {status.charAt(0).toUpperCase() + status.slice(1)}
            </Badge>
        );
    };
    
    const getTypeBadge = (type) => {
        return (
            <Badge className={`rounded-none ${type === 'quote' ? 'bg-primary/10 text-primary' : 'bg-secondary text-secondary-foreground'}`}>
                {type === 'quote' ? 'Quote Request' : 'Guidance'}
            </Badge>
        );
    };
    
    return (
        <div data-testid="admin-quotes">
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h1 className="font-heading font-black text-3xl tracking-tight mb-2">Quote Requests</h1>
                    <p className="text-muted-foreground">Manage incoming quote and guidance requests</p>
                </div>
                <div className="flex items-center gap-4">
                    <Select value={statusFilter || "all"} onValueChange={handleStatusChange}>
                        <SelectTrigger className="w-40 rounded-none bg-input border-transparent" data-testid="status-filter">
                            <SelectValue placeholder="All Status" />
                        </SelectTrigger>
                        <SelectContent className="bg-card border-border rounded-none">
                            <SelectItem value="all" className="rounded-none">All Status</SelectItem>
                            <SelectItem value="new" className="rounded-none">New</SelectItem>
                            <SelectItem value="contacted" className="rounded-none">Contacted</SelectItem>
                            <SelectItem value="quoted" className="rounded-none">Quoted</SelectItem>
                            <SelectItem value="closed" className="rounded-none">Closed</SelectItem>
                        </SelectContent>
                    </Select>
                    <Button variant="outline" onClick={fetchQuotes} className="rounded-none">
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Refresh
                    </Button>
                </div>
            </div>
            
            {/* Quotes Table */}
            <Card className="bg-card border border-border rounded-none">
                <CardContent className="p-0">
                    {loading ? (
                        <div className="p-8 text-center text-muted-foreground">Loading...</div>
                    ) : quotes.length === 0 ? (
                        <div className="p-8 text-center text-muted-foreground">
                            No quote requests found.
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow className="border-border bg-secondary/50">
                                    <TableHead className="font-mono uppercase text-xs">Date</TableHead>
                                    <TableHead className="font-mono uppercase text-xs">Type</TableHead>
                                    <TableHead className="font-mono uppercase text-xs">Contact</TableHead>
                                    <TableHead className="font-mono uppercase text-xs">Product</TableHead>
                                    <TableHead className="font-mono uppercase text-xs">Status</TableHead>
                                    <TableHead className="font-mono uppercase text-xs text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {quotes.map((quote) => (
                                    <TableRow key={quote.id} className="border-border">
                                        <TableCell className="font-mono text-sm text-muted-foreground">
                                            {format(new Date(quote.created_at), "MMM d, yyyy")}
                                        </TableCell>
                                        <TableCell>{getTypeBadge(quote.request_type)}</TableCell>
                                        <TableCell>
                                            <div>
                                                <p className="font-bold">{quote.name}</p>
                                                <p className="text-muted-foreground text-sm">{quote.email}</p>
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-muted-foreground">
                                            {quote.product_name || "General Inquiry"}
                                        </TableCell>
                                        <TableCell>{getStatusBadge(quote.status)}</TableCell>
                                        <TableCell className="text-right">
                                            <Button variant="ghost" size="sm" onClick={() => handleView(quote)} data-testid={`view-quote-${quote.id}`}>
                                                <Eye className="h-4 w-4" />
                                            </Button>
                                            <Button variant="ghost" size="sm" onClick={() => handleDelete(quote.id)} className="text-destructive hover:text-destructive">
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>
            
            {/* View Quote Dialog */}
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogContent className="bg-card border-border rounded-none max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="font-heading font-bold text-xl flex items-center gap-3">
                            Quote Request Details
                            {selectedQuote && getStatusBadge(selectedQuote.status)}
                        </DialogTitle>
                    </DialogHeader>
                    
                    {selectedQuote && (
                        <div className="space-y-6 py-4">
                            {/* Contact Info */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="flex items-start gap-3">
                                    <div className="w-10 h-10 bg-secondary flex items-center justify-center">
                                        <Mail className="h-5 w-5 text-muted-foreground" />
                                    </div>
                                    <div>
                                        <p className="font-bold">{selectedQuote.name}</p>
                                        <a href={`mailto:${selectedQuote.email}`} className="text-primary hover:underline text-sm">
                                            {selectedQuote.email}
                                        </a>
                                    </div>
                                </div>
                                
                                {selectedQuote.phone && (
                                    <div className="flex items-start gap-3">
                                        <div className="w-10 h-10 bg-secondary flex items-center justify-center">
                                            <Phone className="h-5 w-5 text-muted-foreground" />
                                        </div>
                                        <div>
                                            <p className="text-muted-foreground text-sm">Phone</p>
                                            <a href={`tel:${selectedQuote.phone}`} className="text-primary hover:underline">
                                                {selectedQuote.phone}
                                            </a>
                                        </div>
                                    </div>
                                )}
                                
                                {selectedQuote.company && (
                                    <div className="flex items-start gap-3">
                                        <div className="w-10 h-10 bg-secondary flex items-center justify-center">
                                            <Building className="h-5 w-5 text-muted-foreground" />
                                        </div>
                                        <div>
                                            <p className="text-muted-foreground text-sm">Company</p>
                                            <p>{selectedQuote.company}</p>
                                        </div>
                                    </div>
                                )}
                                
                                <div className="flex items-start gap-3">
                                    <div className="w-10 h-10 bg-secondary flex items-center justify-center">
                                        <Calendar className="h-5 w-5 text-muted-foreground" />
                                    </div>
                                    <div>
                                        <p className="text-muted-foreground text-sm">Submitted</p>
                                        <p>{format(new Date(selectedQuote.created_at), "MMMM d, yyyy 'at' h:mm a")}</p>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Request Details */}
                            <div className="space-y-4 pt-4 border-t border-border">
                                <div className="flex items-center gap-2">
                                    {getTypeBadge(selectedQuote.request_type)}
                                    {selectedQuote.product_name && (
                                        <span className="text-muted-foreground">for {selectedQuote.product_name}</span>
                                    )}
                                </div>
                                
                                {selectedQuote.application && (
                                    <div>
                                        <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-1">Application</p>
                                        <p>{selectedQuote.application}</p>
                                    </div>
                                )}
                                
                                {selectedQuote.message && (
                                    <div>
                                        <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-1">Message</p>
                                        <div className="bg-secondary/50 p-4 border-l-2 border-primary">
                                            <p className="whitespace-pre-wrap">{selectedQuote.message}</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                            
                            {/* Notes */}
                            <div className="space-y-2 pt-4 border-t border-border">
                                <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Internal Notes</p>
                                <Textarea
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    className="rounded-none bg-input border-transparent focus:border-primary"
                                    rows={3}
                                    placeholder="Add internal notes about this request..."
                                />
                            </div>
                            
                            {/* Status Actions */}
                            <div className="flex flex-wrap gap-2 pt-4 border-t border-border">
                                <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground w-full mb-2">Update Status</p>
                                <Button 
                                    variant={selectedQuote.status === 'new' ? 'default' : 'outline'}
                                    onClick={() => handleUpdateStatus('new')}
                                    className="rounded-none"
                                    size="sm"
                                >
                                    New
                                </Button>
                                <Button 
                                    variant={selectedQuote.status === 'contacted' ? 'default' : 'outline'}
                                    onClick={() => handleUpdateStatus('contacted')}
                                    className="rounded-none"
                                    size="sm"
                                >
                                    Contacted
                                </Button>
                                <Button 
                                    variant={selectedQuote.status === 'quoted' ? 'default' : 'outline'}
                                    onClick={() => handleUpdateStatus('quoted')}
                                    className="rounded-none"
                                    size="sm"
                                >
                                    Quoted
                                </Button>
                                <Button 
                                    variant={selectedQuote.status === 'closed' ? 'default' : 'outline'}
                                    onClick={() => handleUpdateStatus('closed')}
                                    className="rounded-none"
                                    size="sm"
                                >
                                    Closed
                                </Button>
                            </div>
                        </div>
                    )}
                    
                    <DialogFooter>
                        <DialogClose asChild>
                            <Button variant="outline" className="rounded-none">Close</Button>
                        </DialogClose>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AdminQuotes;
